import { jsx, jsxs } from 'react/jsx-runtime';
import { createContext, useContext } from 'react';
import { styled } from 'styled-components';
import { Button } from '@fabric-app/ui/components/Button';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { Flex } from '@fabric-app/ui/components/Flex';
import Select from '@fabric-app/ui/components/Select';
import { ArrowDownIcon } from '@fabric-app/icons/icons';

const SortContext = /* @__PURE__ */ createContext(void 0);
const useSortContext = () => {
  const context = useContext(SortContext);
  if (!context) {
    throw new Error("Sort components must be used within a Sort.Root");
  }
  return context;
};
const OrderButton = styled(Button).attrs({
  variant: "bg-secondary",
  shape: "square"
}).withConfig({
  displayName: "Sort__OrderButton",
  componentId: "sc-82323b58-0"
})([
  `color:`,
  `;font-size:0.9rem;--sort-order-initial:0deg;--sort-order-reverse:180deg;&[data-reverse='true']{--sort-order-initial:180deg;--sort-order-reverse:0deg;}svg{transition:transform 0.2s;transform:rotate(var(--sort-order-initial));}&[data-value='ASC'] svg{transform:rotate(var(--sort-order-reverse));}`
], cssVar["color-text-tertiary"]);
const Root = ({ value, onChange, options, disabledFields, children }) => {
  const { isMobileView } = useResponsive();
  return /* @__PURE__ */ jsx(SortContext.Provider, {
    value: {
      value,
      onChange,
      options,
      disabledFields
    },
    children: /* @__PURE__ */ jsx(Flex, {
      gap: isMobileView ? "text" : "elementsContainer",
      children
    })
  });
};
const Order = () => {
  const { value, onChange, options } = useSortContext();
  const onToggleOrder = (e) => {
    onChange({
      field: value.field,
      order: value.order === "ASC" ? "DESC" : "ASC"
    });
    e.currentTarget.blur();
  };
  const currentSortOption = options.find((option) => option.value === value.field);
  return /* @__PURE__ */ jsx(OrderButton, {
    "data-value": value.order,
    "data-reverse": currentSortOption?.reverseOrder ? "true" : void 0,
    onClick: onToggleOrder,
    children: /* @__PURE__ */ jsx(ArrowDownIcon, {})
  });
};
const Field = () => {
  const { value, onChange, options, disabledFields } = useSortContext();
  const availableOptions = options.filter((option) => !disabledFields?.includes(option.value));
  const onChangeField = (field) => {
    const newSort = availableOptions.find((option) => option.value === field);
    onChange({
      field,
      order: newSort?.defaultOrder ?? value.order
    });
  };
  return /* @__PURE__ */ jsx(Select, {
    value: value.field,
    onChange: (value2) => onChangeField(value2),
    selectVariant: "default",
    triggerProps: {
      hideOptionIcon: true
    },
    title: "Sort by...",
    children: /* @__PURE__ */ jsx(Select.ItemGroup, {
      children: availableOptions.map((option) => /* @__PURE__ */ jsxs(Select.ItemOption, {
        value: option.value,
        children: [
          /* @__PURE__ */ jsx(Select.ItemOptionIconContainer, {
            children: /* @__PURE__ */ jsx(option.Icon, {})
          }),
          option.label
        ]
      }, option.value))
    })
  });
};
const Sort = {
  Root,
  Order,
  Field
};

export { Sort };
//# sourceMappingURL=Sort.js.map
